import React, { useEffect, useState } from 'react';
import Modal from './ui/Modal';
import { useTranslation } from '../hooks/useTranslation';
import type { ExcelExportAudience } from '../utils/excelExport';

interface ExportBiddingExcelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (audience: ExcelExportAudience) => void | Promise<void>;
}

const ExportBiddingExcelModal: React.FC<ExportBiddingExcelModalProps> = ({
  isOpen,
  onClose,
  onExport,
}) => {
  const { t } = useTranslation();
  const [audience, setAudience] = useState<ExcelExportAudience>('client');
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setAudience('client');
      setError(null);
      setIsExporting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isExporting) return;
    setError(null);
    onClose();
  };

  const handleExport = async () => {
    setError(null);
    setIsExporting(true);
    try {
      await onExport(audience);
      onClose();
    } catch (err) {
      console.error(err);
      setError(t('bidding.exportError') || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('bidding.exportExcelTitle') || 'Export Excel'}
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-400">
          {t('bidding.exportExcelIntro') || 'Choose who this file is for.'}
        </p>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={isExporting}
            onClick={() => setAudience('client')}
            className={`text-left px-4 py-3 rounded-lg border transition-colors ${
              audience === 'client'
                ? 'border-indigo-500 bg-indigo-600/20 text-white'
                : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <div className="font-medium">
              {t('bidding.exportExcelForClient') || 'For client'}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {t('bidding.exportExcelForClientHint') ||
                'Single sheet “Смета” — no rates or internal tabs'}
            </div>
          </button>

          <button
            type="button"
            disabled={isExporting}
            onClick={() => setAudience('internal')}
            className={`text-left px-4 py-3 rounded-lg border transition-colors ${
              audience === 'internal'
                ? 'border-indigo-500 bg-indigo-600/20 text-white'
                : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <div className="font-medium">
              {t('bidding.exportExcelForInternal') || 'For internal use'}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {t('bidding.exportExcelForInternalHint') ||
                'Full workbook: shots, rates, summary (revenue + expense)'}
            </div>
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-400">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            disabled={isExporting}
            onClick={handleClose}
            className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            disabled={isExporting}
            onClick={handleExport}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm disabled:opacity-50"
          >
            {isExporting
              ? (t('bidding.exportExcelExporting') || 'Exporting…')
              : (t('bidding.exportExcel') || 'Export Excel')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ExportBiddingExcelModal;
